/* =========================================================================
   Penny Premium 3D — pet-trail.js
   Two on-brand delighters for a cat store, both cheap and gated:

   1. Paw-print cursor trail (desktop, fine pointer, motion-full only):
      as the mouse travels, little paw prints stamp along the path —
      alternating left/right like a real gait, rotated to the direction of
      travel — then fade away. Pooled nodes + Web Animations API.

   2. Scroll cat (all devices, reduced-motion keeps it but without the
      walk bob): a thin sun-yellow progress line along the top of the
      viewport with a small cat silhouette walking to match scroll
      progress; it flips to face the direction you scroll.

   Everything is created from JS — no template markup needed — and every
   element is pointer-events: none.
   ========================================================================= */

const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const PAW =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath fill='%23FFD829' d='M12 12.4c2.7 0 5.6 2.1 5.6 4.8 0 1.7-1.3 2.9-3 2.9-1 0-1.8-.5-2.6-.5s-1.6.5-2.6.5c-1.7 0-3-1.2-3-2.9 0-2.7 2.9-4.8 5.6-4.8zM6 8.1c1.1.3 1.7 1.5 1.4 2.8-.3 1.2-1.4 2-2.4 1.7-1.1-.3-1.7-1.5-1.4-2.8.3-1.2 1.3-2 2.4-1.7zm12 0c1.1-.3 2.1.5 2.4 1.7.3 1.3-.3 2.5-1.4 2.8-1 .3-2.1-.5-2.4-1.7-.3-1.3.3-2.5 1.4-2.8zM9.3 3.8c1.2 0 2 1.1 2 2.4s-.8 2.4-2 2.4-2-1.1-2-2.4.8-2.4 2-2.4zm5.4 0c1.2 0 2 1.1 2 2.4s-.8 2.4-2 2.4-2-1.1-2-2.4.8-2.4 2-2.4z'/%3E%3C/svg%3E";

const CAT =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 40 26'%3E%3Cpath fill='%23FFD829' d='M27 8c0-2 .4-4 1.4-5.6.3-.4.9-.5 1.2 0l1.7 2.5c.4.5 1 .8 1.6.8h1.2c.6 0 1.3-.3 1.6-.8L37.4 2.4c.3-.5 1-.4 1.2 0C39.6 4 40 6 40 8c0 3.2-1.6 5.6-4 6.7V20c0 2.8-2.2 5-5 5H13c-4.4 0-8-3.6-8-8v-1.5c0-.8-.7-1.5-1.5-1.5S2 14.7 2 15.5V17c0 .6-.4 1-1 1s-1-.4-1-1v-1.5C0 13.6 1.6 12 3.5 12S7 13.6 7 15.5V17c0 3.3 2.7 6 6 6h.5c-1-1.3-1.5-3-1.5-4.7 0-2.6 1.2-5 3.2-6.6C17.4 10 20 9 23 8.7c1.3-.1 2.7-.4 4-.7z'/%3E%3C/svg%3E";

/* --- 1. Paw-print cursor trail ------------------------------------------- */
function initPawTrail() {
  const fine = window.matchMedia('(pointer: fine)').matches;
  const motionOk = document.documentElement.classList.contains('motion-full');
  if (!fine || !motionOk || reduced) return;

  const layer = document.createElement('div');
  layer.className = 'paw-trail';
  layer.setAttribute('aria-hidden', 'true');
  document.body.appendChild(layer);

  const POOL = 18;
  const steps = [];
  for (let i = 0; i < POOL; i++) {
    const s = document.createElement('span');
    s.className = 'paw-trail__step';
    layer.appendChild(s);
    steps.push(s);
  }

  let cursor = 0;
  let lastX = -1;
  let lastY = -1;
  let side = 1;
  const GAP = 92; // px of travel between stamps

  window.addEventListener(
    'pointermove',
    (event) => {
      if (event.pointerType && event.pointerType !== 'mouse') return;
      if (lastX < 0) {
        lastX = event.clientX;
        lastY = event.clientY;
        return;
      }
      const dx = event.clientX - lastX;
      const dy = event.clientY - lastY;
      const dist = Math.hypot(dx, dy);
      if (dist < GAP) return;

      const heading = Math.atan2(dy, dx);
      side = -side;
      // offset perpendicular to travel, like alternating left/right paws
      const ox = Math.cos(heading + Math.PI / 2) * 10 * side;
      const oy = Math.sin(heading + Math.PI / 2) * 10 * side;

      const s = steps[cursor];
      cursor = (cursor + 1) % POOL;
      s.style.left = event.clientX + ox - 9 + 'px';
      s.style.top = event.clientY + oy - 9 + 'px';
      s.style.transform = 'rotate(' + (heading * 180 / Math.PI + 90).toFixed(0) + 'deg)';
      s.animate(
        [
          { opacity: 0.55, scale: '0.7' },
          { opacity: 0.4, scale: '1', offset: 0.25 },
          { opacity: 0, scale: '1.05' },
        ],
        { duration: 1100, easing: 'ease-out', fill: 'forwards' }
      );

      lastX = event.clientX;
      lastY = event.clientY;
    },
    { passive: true }
  );
}

/* --- 2. Scroll cat: progress line + walking cat --------------------------- */
function initScrollCat() {
  const rail = document.createElement('div');
  rail.className = 'scroll-cat';
  rail.setAttribute('aria-hidden', 'true');
  rail.innerHTML =
    '<span class="scroll-cat__line"></span><span class="scroll-cat__walker"></span>';
  document.body.appendChild(rail);

  const line = rail.firstElementChild;
  const walker = rail.lastElementChild;
  let idleTimer;
  let lastY = window.scrollY;

  const update = () => {
    const max = document.documentElement.scrollHeight - window.innerHeight;
    const progress = max > 0 ? Math.min(window.scrollY / max, 1) : 0;
    const x = progress * (window.innerWidth - 34);
    line.style.width = (progress * 100).toFixed(2) + '%';
    const dir = window.scrollY >= lastY ? 1 : -1;
    lastY = window.scrollY;
    walker.style.transform = 'translateX(' + x.toFixed(0) + 'px) scaleX(' + dir + ')';
    if (!reduced) {
      walker.classList.add('is-walking');
      clearTimeout(idleTimer);
      idleTimer = setTimeout(() => walker.classList.remove('is-walking'), 240);
    }
  };

  window.addEventListener('scroll', update, { passive: true });
  window.addEventListener('resize', update);
  update();
}

const style = document.createElement('style');
style.textContent =
  '.paw-trail{position:fixed;inset:0;overflow:hidden;pointer-events:none;z-index:59}' +
  '.paw-trail__step{position:absolute;width:18px;height:18px;opacity:0;' +
  'background:url("' + PAW + '") center/contain no-repeat;' +
  'filter:drop-shadow(0 0 6px rgb(255 216 41 / 0.35))}' +
  '.scroll-cat{position:fixed;top:0;left:0;right:0;height:0;pointer-events:none;z-index:59}' +
  '.scroll-cat__line{position:absolute;top:0;left:0;height:2px;width:0;' +
  'background:linear-gradient(90deg,rgb(255 216 41/0.25),#FFD829);' +
  'box-shadow:0 0 10px rgb(255 216 41/0.45)}' +
  '.scroll-cat__walker{position:absolute;top:1px;left:0;width:34px;height:22px;' +
  'background:url("' + CAT + '") center/contain no-repeat;' +
  'filter:drop-shadow(0 0 8px rgb(255 216 41/0.4))}' +
  '.scroll-cat__walker.is-walking{animation:scroll-cat-bob 240ms ease-in-out infinite}' +
  '@keyframes scroll-cat-bob{0%,100%{margin-top:0}50%{margin-top:-2px}}';
document.head.appendChild(style);

initPawTrail();
initScrollCat();
